/**
 * The approve / revoke confirmation, with an optional note — shared by the
 * report page's Admin menu and the workbench's Document view. The caller
 * owns the approvals hook; this owns only the dialog and its note.
 */

import { useState } from 'react';

import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';

import type { ApprovalStatus } from '@/types/report';

interface ApprovalModalProps {
  opened: boolean;
  onClose: () => void;
  /** Approved now → the dialog revokes; otherwise it approves. */
  approved: boolean;
  mutating: boolean;
  /** Approve or revoke with the note (if any). Errors are the hook's to
   * notify; the dialog stays open when this throws. */
  onConfirm: (notes?: string) => Promise<unknown>;
}

/** "Approved" / "Approval revoked" / "Pending approval". */
export function approvalLabel(status: ApprovalStatus | null): string {
  if (status === 'approved') {
    return 'Approved';
  }
  return status === 'revoked' ? 'Approval revoked' : 'Pending approval';
}

export function ApprovalModal({
  opened,
  onClose,
  approved,
  mutating,
  onConfirm,
}: Readonly<ApprovalModalProps>) {
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);

  const close = () => {
    setNotes('');
    setShowNotes(false);
    onClose();
  };

  const confirm = async () => {
    try {
      await onConfirm(notes.trim() || undefined);
      close();
    } catch {
      // handled by the approvals hook's notifications
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={approved ? 'Revoke this approval?' : 'Approve this report for clients?'}
      centered
    >
      <Stack gap="sm">
        {!showNotes ? (
          <Text
            size="xs"
            c="dimmed"
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setShowNotes(true)}
          >
            Add a note (optional)
          </Text>
        ) : (
          <Textarea
            placeholder="Optional notes"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            rows={3}
            size="xs"
          />
        )}
        <Group justify="flex-end" gap="xs">
          <Button size="xs" variant="subtle" onClick={close}>
            Cancel
          </Button>
          <Button
            size="xs"
            color={approved ? 'red' : 'green'}
            onClick={() => void confirm()}
            loading={mutating}
            data-testid="confirm-approval-action"
          >
            {approved ? 'Revoke approval' : 'Approve'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
